import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { brl, fmtDate, todayISO } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField, TextArea, SelectField, MoneyField } from "@/components/FormKit";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Loader2, History, CalendarDays, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = [
  { value: "corrente", label: "Conta Corrente" }, { value: "poupanca", label: "Poupança" },
  { value: "investimento", label: "Investimento" }, { value: "caixa", label: "Caixa" }, { value: "outros", label: "Outros" },
];
const EMPTY = { name: "", bank: "", agency: "", account_number: "", type: "corrente", initial_balance: 0, initial_balance_date: todayISO(), notes: "", status: "ativa" };

export default function AccountsPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [txOpen, setTxOpen] = useState(false);
  const [txs, setTxs] = useState([]);
  const [txAccount, setTxAccount] = useState(null);
  const [asOf, setAsOf] = useState("");
  const [asOfBal, setAsOfBal] = useState({});
  const [loadingBal, setLoadingBal] = useState(false);

  const load = () => { setLoading(true); api.get("/accounts").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!asOf) { setAsOfBal({}); return; }
    setLoadingBal(true);
    api.get(`/accounts/balances?as_of=${asOf}`)
      .then((r) => setAsOfBal(Object.fromEntries(r.data.map((x) => [x.id, x.balance]))))
      .catch(() => toast.error("Não foi possível calcular o saldo na data."))
      .finally(() => setLoadingBal(false));
  }, [asOf]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) await api.put(`/accounts/${editing.id}`, form);
      else await api.post("/accounts", form);
      toast.success("Conta financeira salva."); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const openTx = async (acc) => {
    setTxAccount(acc); setTxOpen(true);
    const { data } = await api.get(`/transactions?account_id=${acc.id}`); setTxs(data);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => [r.name, r.bank].some((v) => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);
  const canEdit = hasPermission("accounts.update");
  const typeLabel = (t) => TYPES.find((x) => x.value === t)?.label || t;
  const rowBal = (r) => (asOf ? (asOfBal[r.id] ?? 0) : (r.current_balance || 0));
  const totalBal = filtered.reduce((s, r) => s + rowBal(r), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Contas Financeiras" subtitle="Contas bancárias e caixas do clube. O saldo atual é calculado pelas movimentações." />
      <Toolbar search={search} setSearch={setSearch} searchTestId="acc-search"
        onNew={hasPermission("accounts.create") ? () => { setEditing(null); setForm(EMPTY); setOpen(true); } : null}
        newLabel="Nova Conta Financeira" newTestId="add-account-button">
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid="accounts-asof-filter">
          <CalendarDays className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span className="text-xs text-slate-400">Saldo em</span>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="accounts-asof-date" className="text-xs bg-transparent focus:outline-none text-slate-600 w-[120px]" />
          {asOf && <button onClick={() => setAsOf("")} data-testid="accounts-asof-clear" className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>}
        </div>
      </Toolbar>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : filtered.length === 0 ? <EmptyState text="Nenhuma conta financeira cadastrada." />
        : (
        <TableShell testId="accounts-table" headers={[{ label: "Conta" }, { label: "Tipo" }, { label: "Banco / Agência" }, { label: asOf ? `Saldo em ${fmtDate(asOf)}` : "Saldo Atual", align: "right" }, { label: "Status" }, { label: "Ações", align: "right" }]}>
          {filtered.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`account-row-${r.id}`}>
              <td className="px-4 py-3.5 font-medium text-slate-800">{r.name}</td>
              <td className="px-4 py-3.5 text-slate-500">{typeLabel(r.type)}</td>
              <td className="px-4 py-3.5 text-slate-500">{r.bank ? `${r.bank}${r.agency ? " · " + r.agency : ""}` : "—"}</td>
              <td className="px-4 py-3.5 text-right font-mono font-semibold text-slate-900" data-testid={`account-balance-${r.id}`}>{loadingBal && asOf ? "…" : brl(rowBal(r))}</td>
              <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5 text-right">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => openTx(r)} data-testid={`account-tx-${r.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><History className="h-4 w-4" /></button>
                  {canEdit && <button onClick={() => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); }} data-testid={`edit-account-${r.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>}
                </div>
              </td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold border-t border-slate-200">
            <td className="px-4 py-3.5 text-slate-700" colSpan={3}>Total {asOf ? `em ${fmtDate(asOf)}` : "atual"}</td>
            <td className="px-4 py-3.5 text-right font-mono text-slate-900" data-testid="accounts-total-balance">{loadingBal && asOf ? "…" : brl(totalBal)}</td>
            <td colSpan={2}></td>
          </tr>
        </TableShell>
      )}

      <FormModal open={open} onOpenChange={setOpen} title={editing ? "Editar Conta Financeira" : "Nova Conta Financeira"} onSubmit={submit} submitting={submitting} testId="account-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Nome" value={form.name} onChange={(v) => set("name", v)} testId="account-name" required className="sm:col-span-2" />
          <SelectField label="Tipo" value={form.type} onChange={(v) => set("type", v)} testId="account-type" options={TYPES} />
          <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} testId="account-status-field" options={[{ value: "ativa", label: "Ativa" }, { value: "inativa", label: "Inativa" }]} />
          <TextField label="Banco" value={form.bank} onChange={(v) => set("bank", v)} testId="account-bank" />
          <TextField label="Agência" value={form.agency} onChange={(v) => set("agency", v)} testId="account-agency" />
          <TextField label="Conta" value={form.account_number} onChange={(v) => set("account_number", v)} testId="account-number" />
          <div />
          <MoneyField label="Saldo Inicial" value={form.initial_balance} onChange={(v) => set("initial_balance", v)} testId="account-initial-balance" />
          <TextField label="Data do Saldo Inicial" type="date" value={form.initial_balance_date} onChange={(v) => set("initial_balance_date", v)} testId="account-initial-date" />
          <TextArea label="Observações" value={form.notes} onChange={(v) => set("notes", v)} testId="account-notes" className="sm:col-span-2" />
        </div>
      </FormModal>

      <Dialog open={txOpen} onOpenChange={setTxOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="account-tx-modal">
          <DialogHeader><DialogTitle className="font-heading">Movimentações — {txAccount?.name}</DialogTitle></DialogHeader>
          {txs.length === 0 ? <EmptyState text="Nenhuma movimentação registrada." /> : (
            <div className="divide-y divide-slate-100">
              {txs.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{t.description}</div>
                    <div className="text-xs text-slate-400">{fmtDate(t.date)}</div>
                  </div>
                  <div className={`font-mono font-semibold text-sm ${t.type === "entrada" ? "text-emerald-700" : "text-rose-600"}`}>
                    {t.type === "entrada" ? "+" : "−"} {brl(t.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
